import { Jobs, Degrees } from "../../api/Person";


type JobWithDates = Jobs & { startDate?: string; endDate?: string };
type DegreeWithDates = Degrees & { startDate?: string; endDate?: string };

/** Get start/end date from job - API may return snake_case or camelCase. */
function getJobDates(job: JobWithDates) {
  const start = job.start_date ?? job.startDate ?? "";
  const end = job.end_date ?? job.endDate ?? "";
  return { start, end };
}

/** Shared title+employer formatting: "title {separator} employer" with fallbacks when one is missing. */
function formatTitleEmployer(
  job: { title?: string | null; employer?: string | null },
  separator: string
): string {
  const title = job.title?.trim() ?? "";
  const employer = job.employer?.trim() ?? "";
  if (title && employer) return `${title} ${separator} ${employer}`;
  if (title) return title;
  if (employer) return employer;
  return "";
}

/** Resume-style: date range then title then company; current to oldest. */
export function formatJob(job: JobWithDates): string {
  const { start, end } = getJobDates(job);
  let range = "";
  if (start && end) {
    range = `${start} - ${end}`;
  } else if (start) {
    range = start;
  } else if (end) {
    range = end;
  }
  const title = formatTitleEmployer(job, "at");
  const line = [range, title].filter(Boolean).join("\n");
  return line;
}

/** Display format for employment entry: "Title | Employer" (no date range). */
export function formatEmploymentEntry(job: Jobs): string {
  return formatTitleEmployer(job, "|");
}

/** Get start/end date from degree - API may return snake_case or camelCase. */
function getDegreeDates(deg: DegreeWithDates) {
  const start = deg.start_date ?? deg.startDate ?? "";
  const end = deg.end_date ?? deg.endDate ?? "";
  return { start, end };
}

/** Date range then degree and school; most current to oldest. */
export function formatDegree(deg: DegreeWithDates): string {
  const { start, end } = getDegreeDates(deg);
  let range = "";
  if (start && end) {
    range = `${start} - ${end}`;
  } else if (start) {
    range = start;
  } else if (end) {
    range = end;
  }
  const name =
    deg.name && deg.educator
      ? `${deg.name} at ${deg.educator}`
      : deg.name || deg.educator || "";
  const line = [range, name].filter(Boolean).join("\n");
  return line;
}
